/**
 * useCompanies - React Query hooks for business company operations (SESSION_004)
 *
 * Provides hooks for:
 * - Fetching company suggestions (query)
 * - Fetching companies by region (query)
 * - Fetching company details by ID (query)
 * - Creating companies (mutation)
 * - Bulk creating companies from suggestions (mutation)
 * - Updating companies (mutation)
 * - Deleting companies (mutation)
 * - Adding businesses to companies (mutation)
 * - Removing businesses from companies (mutation)
 *
 * Backend endpoints created in SESSION_003 (Sprint 014 Phase 3).
 */

import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query';
import { companyApi, type GetCompanySuggestionsParams, type GetCompaniesByRegionParams, type GetCompanyByIdParams, type SetNoPublicEmailResponse } from '@/infrastructure/api/CompanyApi';
import { queryKeys } from './queryKeys';
import { notificationService } from '@/infrastructure/notifications';
import type {
  GetBusinessCompanySuggestionsResponse,
  GetCompaniesByRegionResponse,
  GetCompanyByIdResponse,
  CreateBusinessCompanyCommand,
  CreateBusinessCompanyResponse,
  UpdateCompanyCommand,
  UpdateCompanyResponse,
  DeleteCompanyResponse,
  AddBusinessToCompanyResponse,
  RemoveBusinessFromCompanyResponse,
  BulkCreateCompaniesFromSuggestionsCommand,
  BulkCreateCompaniesResponse,
  CreateBusinessForCompanyCommand,
  CreateBusinessForCompanyResponse,
  BulkReassignBusinessesRequest,
  BulkReassignBusinessesResponse
} from '@/domain/entities/Company';

/**
 * Query keys for business companies
 * Used for cache invalidation and refetching
 */
export const companyKeys = {
  all: ['companies'] as const,
  suggestions: (regionId: string, params?: GetCompanySuggestionsParams) =>
    [...companyKeys.all, 'suggestions', regionId, params] as const,
  lists: () => [...companyKeys.all, 'list'] as const,
  list: (regionId: string, params?: GetCompaniesByRegionParams) =>
    [...companyKeys.lists(), regionId, params] as const,
  details: () => [...companyKeys.all, 'detail'] as const,
  detail: (id: string, params?: GetCompanyByIdParams) =>
    [...companyKeys.details(), id, params] as const
};

/**
 * useCompanySuggestions - Query hook for fetching company suggestions
 *
 * Features:
 * - Auto-detect company suggestions using name similarity algorithm
 * - Configurable minimum group size and confidence threshold
 * - Cache management (5 min stale time)
 *
 * @param regionId - Region ID to analyze for companies
 * @param params - Query parameters (minGroupSize, confidenceThreshold)
 * @param options - Query options (e.g., { enabled: boolean })
 * @returns React Query result with company suggestions
 *
 * @example
 * const { data, isLoading } = useCompanySuggestions('region-id', {
 *   minGroupSize: 5,
 *   confidenceThreshold: 85
 * }, { enabled: true });
 */
export function useCompanySuggestions(
  regionId: string,
  params?: GetCompanySuggestionsParams,
  options?: { enabled?: boolean }
): UseQueryResult<GetBusinessCompanySuggestionsResponse, Error> {
  // Convert confidenceThreshold from 0-100 to 0.0-1.0 for backend
  const apiParams = params ? {
    ...params,
    confidenceThreshold: params.confidenceThreshold !== undefined
      ? params.confidenceThreshold / 100
      : undefined
  } : undefined;

  return useQuery<GetBusinessCompanySuggestionsResponse, Error>({
    queryKey: companyKeys.suggestions(regionId, params),
    queryFn: () => companyApi.getCompanySuggestions(regionId, apiParams),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes - suggestions don't change frequently
    retry: 2,
    retryDelay: (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
}

/**
 * useCompaniesByRegion - Query hook for fetching companies by region (paginated)
 *
 * Features:
 * - Paginated list of companies for a region
 * - Search by company name
 * - Cache management (5 min stale time)
 * - Automatic refetch on window focus
 *
 * @param regionId - Region ID to get companies for
 * @param params - Query parameters (page, pageSize, searchTerm)
 * @param options - Query options (e.g., { enabled: boolean })
 * @returns React Query result with paginated companies
 *
 * @example
 * const { data } = useCompaniesByRegion('region-id', {
 *   page: 1,
 *   pageSize: 20,
 *   searchTerm: 'Starbucks'
 * });
 */
export function useCompaniesByRegion(
  regionId: string,
  params?: GetCompaniesByRegionParams,
  options?: { enabled?: boolean }
): UseQueryResult<GetCompaniesByRegionResponse, Error> {
  return useQuery<GetCompaniesByRegionResponse, Error>({
    queryKey: companyKeys.list(regionId, params),
    queryFn: () => companyApi.getCompaniesByRegion(regionId, params),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 2,
    retryDelay: (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
}

/**
 * useCompanyById - Query hook for fetching company details by ID
 *
 * Features:
 * - Detailed company information
 * - Paginated list of member businesses
 * - Cache management (5 min stale time)
 * - Automatic refetch on window focus
 *
 * @param id - Company ID
 * @param params - Query parameters (businessPage, businessPageSize)
 * @param options - Query options (e.g., { enabled: boolean })
 * @returns React Query result with company details
 *
 * @example
 * const { data: company, isLoading } = useCompanyById('company-id', {
 *   businessPage: 1,
 *   businessPageSize: 20
 * });
 */
export function useCompanyById(
  id: string,
  params?: GetCompanyByIdParams,
  options?: { enabled?: boolean; showErrorToast?: boolean }
): UseQueryResult<GetCompanyByIdResponse, Error> {
  const showErrorToast = options?.showErrorToast ?? true;
  return useQuery<GetCompanyByIdResponse, Error>({
    queryKey: companyKeys.detail(id, params),
    queryFn: () => companyApi.getCompanyById(id, params, { showErrorToast }),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: (failureCount: number, error: Error): boolean => {
      // Don't retry 404s (company may not exist in this environment)
      if (error.message?.includes('404') || error.message?.includes('not found')) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
}

/**
 * useCreateCompany - Mutation hook for creating a new company
 *
 * Features:
 * - Create single company (without businesses initially)
 * - Automatic query invalidation
 * - Success/error toast notifications
 *
 * @returns Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const createMutation = useCreateCompany();
 *
 * await createMutation.mutateAsync({
 *   name: 'Starbucks',
 *   regionId: 'region-id',
 *   description: 'Coffee company'
 * });
 */
export function useCreateCompany(): UseMutationResult<
  CreateBusinessCompanyResponse,
  Error,
  CreateBusinessCompanyCommand
> {
  const queryClient = useQueryClient();

  return useMutation<CreateBusinessCompanyResponse, Error, CreateBusinessCompanyCommand>({
    mutationFn: (command: CreateBusinessCompanyCommand) => companyApi.createCompany(command),
    onSuccess: (response: CreateBusinessCompanyResponse) => {
      // Invalidate company lists to refetch data
      void queryClient.invalidateQueries({
        queryKey: companyKeys.lists()
      });

      // Show success notification
      notificationService.showSuccess(response.message || 'Company created successfully');
    },
    onError: (error: Error) => {
      // Error notification is already shown by ApiClient
      console.error('Failed to create company:', error);
    }
  });
}

/**
 * useBulkCreateCompanies - Mutation hook for bulk creating companies from suggestions
 *
 * Features:
 * - Create multiple companies with business associations
 * - Automatic query invalidation
 * - Success/error toast notifications with counts
 *
 * @returns Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const bulkCreateMutation = useBulkCreateCompanies();
 *
 * await bulkCreateMutation.mutateAsync({
 *   regionId: 'region-id',
 *   suggestions: [
 *     { companyName: 'Starbucks', businessIds: ['id1', 'id2'] },
 *     { companyName: 'McDonald\'s', businessIds: ['id3', 'id4'] }
 *   ]
 * });
 */
export function useBulkCreateCompanies(): UseMutationResult<
  BulkCreateCompaniesResponse,
  Error,
  BulkCreateCompaniesFromSuggestionsCommand
> {
  // NOTE: queryClient removed in SESSION_131 - no query invalidation needed
  // Query invalidation was causing 23+ second freeze, removed per commit dec0267

  return useMutation<BulkCreateCompaniesResponse, Error, BulkCreateCompaniesFromSuggestionsCommand>({
    mutationFn: (command: BulkCreateCompaniesFromSuggestionsCommand) =>
      companyApi.bulkCreateCompanies(command),
    onSuccess: (response: BulkCreateCompaniesResponse) => {
      // DON'T invalidate any queries - companies are created on backend only
      // CompanyManagementPage will show new companies when user navigates there
      // This prevents unnecessary UI re-renders and 23+ second freeze

      // SESSION_132: Add diagnostic logging to confirm error count
      console.log('[SESSION_132 Debug] Response:', {
        companiesCreated: response.companiesCreated,
        businessesAssigned: response.businessesAssigned,
        errorCount: response.errors?.length || 0
      });

      // Show success notification with counts
      const message = response.message ||
        `Created ${response.companiesCreated} companies with ${response.businessesAssigned} businesses`;

      console.time('[SESSION_132] Success notification');
      notificationService.showSuccess(message);
      console.timeEnd('[SESSION_132] Success notification');

      // SESSION_132 FIX: Batch error notifications to prevent 55+ forced reflows
      // Previous implementation: Loop calling showWarning() for each error (causes 24s freeze)
      // New implementation: Single notification with error summary
      if (response.errors && response.errors.length > 0) {
        console.time('[SESSION_132] Error notifications');
        console.log('[SESSION_132 Debug] Processing', response.errors.length, 'errors');

        // Batch errors into single notification to prevent UI freeze
        const errorCount = response.errors.length;
        const maxErrorsToShow = 3;
        const visibleErrors = response.errors.slice(0, maxErrorsToShow);
        const remainingCount = errorCount - maxErrorsToShow;

        let errorMessage = `${errorCount} issue${errorCount > 1 ? 's' : ''} occurred:\n`;
        errorMessage += visibleErrors.map((err) => `• ${err}`).join('\n');

        if (remainingCount > 0) {
          errorMessage += `\n... and ${remainingCount} more issue${remainingCount > 1 ? 's' : ''}`;
        }

        // Single warning notification instead of loop (prevents forced reflow storm)
        notificationService.showWarning(errorMessage);
        console.timeEnd('[SESSION_132] Error notifications');
      }
    },
    onError: (error: Error) => {
      // Error notification is already shown by ApiClient
      console.error('Failed to bulk create companies:', error);
    }
  });
}

/**
 * useUpdateCompany - Mutation hook for updating a company
 *
 * Features:
 * - Update company name, description, or active status
 * - Optimistic updates
 * - Automatic query invalidation
 * - Success/error toast notifications
 *
 * @returns Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const updateMutation = useUpdateCompany();
 *
 * await updateMutation.mutateAsync({
 *   id: 'company-id',
 *   name: 'Updated Name',
 *   description: 'Updated description',
 *   isActive: true
 * });
 */
export function useUpdateCompany(): UseMutationResult<
  UpdateCompanyResponse,
  Error,
  { id: string; command: UpdateCompanyCommand }
> {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateCompanyResponse,
    Error,
    { id: string; command: UpdateCompanyCommand }
  >({
    mutationFn: async ({ id, command }) => companyApi.updateCompany(id, command),
    onSuccess: (response: UpdateCompanyResponse, variables) => {
      // Invalidate specific company detail and all lists
      void queryClient.invalidateQueries({
        queryKey: companyKeys.detail(variables.id)
      });
      void queryClient.invalidateQueries({
        queryKey: companyKeys.lists()
      });

      // Show success notification
      notificationService.showSuccess(response.message || 'Company updated successfully');
    },
    onError: (error: Error) => {
      // Error notification is already shown by ApiClient
      console.error('Failed to update company:', error);
    }
  });
}

/**
 * useDeleteCompany - Mutation hook for deleting a company (soft delete)
 *
 * Features:
 * - Soft delete company and all business associations
 * - Automatic query invalidation
 * - Success/error toast notifications
 *
 * @returns Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const deleteMutation = useDeleteCompany();
 *
 * await deleteMutation.mutateAsync('company-id');
 */
export function useDeleteCompany(): UseMutationResult<
  DeleteCompanyResponse,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation<DeleteCompanyResponse, Error, string>({
    mutationFn: (id: string) => companyApi.deleteCompany(id),
    onSuccess: (response: DeleteCompanyResponse) => {
      // Invalidate all company queries
      void queryClient.invalidateQueries({
        queryKey: companyKeys.all
      });

      // Show success notification
      notificationService.showSuccess(response.message || 'Company deleted successfully');
    },
    onError: (error: Error) => {
      // Error notification is already shown by ApiClient
      console.error('Failed to delete company:', error);
    }
  });
}

/**
 * useAddBusinessToCompany - Mutation hook for adding a business to a company
 *
 * Features:
 * - Associate business with company
 * - Optimistic updates
 * - Automatic query invalidation
 * - Success/error toast notifications
 *
 * @returns Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const addBusinessMutation = useAddBusinessToCompany();
 *
 * await addBusinessMutation.mutateAsync({
 *   companyId: 'company-id',
 *   businessId: 'business-id'
 * });
 */
export function useAddBusinessToCompany(): UseMutationResult<
  AddBusinessToCompanyResponse,
  Error,
  { companyId: string; businessId: string }
> {
  const queryClient = useQueryClient();

  return useMutation<
    AddBusinessToCompanyResponse,
    Error,
    { companyId: string; businessId: string }
  >({
    mutationFn: async ({ companyId, businessId }) =>
      companyApi.addBusinessToCompany(companyId, businessId),
    onSuccess: (response: AddBusinessToCompanyResponse, variables) => {
      // Invalidate specific company detail to refresh business list
      void queryClient.invalidateQueries({
        queryKey: companyKeys.detail(variables.companyId)
      });
      // Invalidate company lists to update business counts
      void queryClient.invalidateQueries({
        queryKey: companyKeys.lists()
      });

      // Show success notification
      notificationService.showSuccess(response.message || 'Business added to company successfully');
    },
    onError: (error: Error) => {
      // Error notification is already shown by ApiClient
      console.error('Failed to add business to company:', error);
    }
  });
}

/**
 * useRemoveBusinessFromCompany - Mutation hook for removing a business from a company
 *
 * Features:
 * - Disassociate business from company
 * - Optimistic updates
 * - Automatic query invalidation
 * - Success/error toast notifications
 *
 * @returns Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const removeBusinessMutation = useRemoveBusinessFromCompany();
 *
 * await removeBusinessMutation.mutateAsync({
 *   companyId: 'company-id',
 *   businessId: 'business-id'
 * });
 */
export function useRemoveBusinessFromCompany(): UseMutationResult<
  RemoveBusinessFromCompanyResponse,
  Error,
  { companyId: string; businessId: string }
> {
  const queryClient = useQueryClient();

  return useMutation<
    RemoveBusinessFromCompanyResponse,
    Error,
    { companyId: string; businessId: string }
  >({
    mutationFn: async ({ companyId, businessId }) =>
      companyApi.removeBusinessFromCompany(companyId, businessId),
    onSuccess: (response: RemoveBusinessFromCompanyResponse, variables) => {
      // Invalidate specific company detail to refresh business list
      void queryClient.invalidateQueries({
        queryKey: companyKeys.detail(variables.companyId)
      });
      // Invalidate company lists to update business counts
      void queryClient.invalidateQueries({
        queryKey: companyKeys.lists()
      });

      // Show success notification
      notificationService.showSuccess(response.message || 'Business removed from company successfully');
    },
    onError: (error: Error) => {
      // Error notification is already shown by ApiClient
      console.error('Failed to remove business from company:', error);
    }
  });
}

/**
 * useCreateBusinessForCompany - Mutation hook for creating a new business under a company
 *
 * Creates a new business (Place, Amenity, Business, Alias chain) and associates
 * it with the specified company. Used when the desired location doesn't exist in the DB.
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export function useCreateBusinessForCompany(): UseMutationResult<
  CreateBusinessForCompanyResponse,
  Error,
  { companyId: string; command: Omit<CreateBusinessForCompanyCommand, 'companyId'> }
> {
  const queryClient = useQueryClient();

  return useMutation<
    CreateBusinessForCompanyResponse,
    Error,
    { companyId: string; command: Omit<CreateBusinessForCompanyCommand, 'companyId'> }
  >({
    mutationFn: async ({ companyId, command }) =>
      companyApi.createBusinessForCompany(companyId, command),
    onSuccess: (response: CreateBusinessForCompanyResponse, variables) => {
      // Invalidate specific company detail to refresh business list
      void queryClient.invalidateQueries({
        queryKey: companyKeys.detail(variables.companyId)
      });
      // Invalidate company lists to update business counts
      void queryClient.invalidateQueries({
        queryKey: companyKeys.lists()
      });

      // Show success notification
      notificationService.showSuccess(response.message || 'Business created and added to company successfully');
    },
    onError: (error: Error) => {
      // Error notification is already shown by ApiClient
      console.error('Failed to create business for company:', error);
    }
  });
}

/**
 * useBulkReassignBusinesses - Mutation hook for bulk reassigning businesses to a target company
 *
 * Sprint 053: Reassigns multiple businesses to a target company, optionally deleting orphaned companies.
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export function useBulkReassignBusinesses(): UseMutationResult<
  BulkReassignBusinessesResponse,
  Error,
  { targetCompanyId: string; request: BulkReassignBusinessesRequest }
> {
  const queryClient = useQueryClient();

  return useMutation<
    BulkReassignBusinessesResponse,
    Error,
    { targetCompanyId: string; request: BulkReassignBusinessesRequest }
  >({
    mutationFn: async ({ targetCompanyId, request }) =>
      companyApi.bulkReassignBusinesses(targetCompanyId, request),
    onSuccess: (response: BulkReassignBusinessesResponse) => {
      // Invalidate company queries (lists and details)
      void queryClient.invalidateQueries({
        queryKey: companyKeys.all
      });
      // Invalidate business queries to reflect new company assignments
      void queryClient.invalidateQueries({
        queryKey: ['businesses']
      });

      notificationService.showSuccess(response.message || 'Businesses reassigned successfully');

      if (response.errors && response.errors.length > 0) {
        const errorCount = response.errors.length;
        const maxErrorsToShow = 3;
        const visibleErrors = response.errors.slice(0, maxErrorsToShow);
        const remainingCount = errorCount - maxErrorsToShow;

        let errorMessage = `${errorCount} issue${errorCount > 1 ? 's' : ''} occurred:\n`;
        errorMessage += visibleErrors.map((err) => `• ${err}`).join('\n');

        if (remainingCount > 0) {
          errorMessage += `\n... and ${remainingCount} more issue${remainingCount > 1 ? 's' : ''}`;
        }

        notificationService.showWarning(errorMessage);
      }
    },
    onError: (error: Error) => {
      console.error('Failed to bulk reassign businesses:', error);
    }
  });
}

/**
 * useSetCompanyNoPublicEmail - Mutation hook for setting no-public-email flag on a company
 *
 * Sprint 054: Allows admin to mark a company as having no publicly available email.
 * Invalidates company details and mention review caches so badges update.
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export function useSetCompanyNoPublicEmail(): UseMutationResult<
  SetNoPublicEmailResponse,
  Error,
  { companyId: string; noPublicEmailAvailable: boolean }
> {
  const queryClient = useQueryClient();

  return useMutation<
    SetNoPublicEmailResponse,
    Error,
    { companyId: string; noPublicEmailAvailable: boolean }
  >({
    mutationFn: async ({ companyId, noPublicEmailAvailable }) =>
      companyApi.setNoPublicEmailAvailable(companyId, noPublicEmailAvailable),
    onSuccess: (response: SetNoPublicEmailResponse, variables) => {
      // Invalidate company detail to refresh flag state
      void queryClient.invalidateQueries({
        queryKey: companyKeys.detail(variables.companyId)
      });
      // Invalidate company lists
      void queryClient.invalidateQueries({
        queryKey: companyKeys.lists()
      });
      // Invalidate mention review data so email status badges update
      void queryClient.invalidateQueries({
        queryKey: queryKeys.mentions.all
      });

      notificationService.showSuccess(response.message || 'Email availability flag updated');
    },
    onError: (error: Error) => {
      console.error('Failed to set no-public-email flag:', error);
    }
  });
}
